///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { GtthEntityInfoWindowContent } from '../gtth-entity-info-window-content-interface';
import { GtthChangeTypeForSelectedLiksCommand } from '../../engine/commands/link/change-type-for-selected-links-command';
import { GtthKeyValueDTO, GtthStepGroupEnum, NodeTypeEnum } from '../../../../generated/shared-types.model';
import { GtthHistoryService } from '../../engine/history-service';
import { GtthRemoveMultipleEntitiesCommand } from '../../engine/commands/remove-multiple-entities-command';
import { GtthVerifyLosesSelectedEntitiesCommand } from '../../engine/commands/link/verify-loses-for-selected-entities-command';
import { GtthCreateLosesToSelectedEntitiesCommand } from '../../engine/commands/link/create-loses-between-selected-entities-command';
import { GtthDisableMultipleEntitiesCommand } from '../../engine/commands/disable-multiple-entities-command';
import { GtthEnableMultipleEntitiesCommand } from '../../engine/commands/enable-multiple-entities-command';
import { LinkFeature } from '../../entities/link/link-feature';
import { GtthFreezeSelectedLinksCommand } from '../../engine/commands/link/freeze-selected-links-command';
import { GtthHouseDetectionCommand } from '../../engine/commands/node/house-detection-command';
import { GtthNotificationsService } from '../../engine/notifications-service';
import { TopologyLinkTypeEnum } from '../../../../services/project-socket/dto/topology-types.model';
import { GtthNodeFeatureDtoModel } from '../../../../services/project-socket/dto/gtth-node-feature-dto.model';
import { GtthChangeMHTypeMultipleUnitsCommand } from '../../engine/commands/node/change-mh-type-multiple-units-command';
import { GtthUpdateMainConfigCommand } from '../../engine/commands/project/update-main-config-command';
import { PolygonFeature } from '../../entities/polygon/polygon-feature';
import { GtthChangeLayerMultipleUnitsCommand } from '../../engine/commands/node/change-layer-multiple-units-command';
import { GtthLayerDtoModel } from '../../../../services/project-socket/dto/gtth-layer-dto.model';
import { GtthLogicalTopology } from '../../entities/logical-topology';
import { GtthChangeBlockedByPoleMultipleUnitsCommand } from '../../engine/commands/node/change-blocked-by-pole-multiple-units-command';
import { GtthPosition } from '../../entities/position';
import { SelectedAreaInfoTabsEnum } from './selected-area-info-tabs.enum';
import { GtthInfoWindowsService } from '../info-windows-service';
import { GtthChangeDeviceAzimuthMultipleUnitsCommand } from '../../engine/commands/node/change-device-azimuth-multiple-units-command';
import { NodeFeature } from '../../entities/node/node-feature';
import { SuiEditableSelectOption, SuiTypeEditableSelect, SuiTypeNumber } from '@siklu/sui-lib/infrastructure';
import { GtthProjectStateService } from '../../engine/project-state.service';
import { isDefined } from '@siklu/sui-lib/utils';
import { FormsModule } from '@angular/forms';
import { SuiTranslateEnumPipe, SuiTranslatePipe } from '@siklu/sui-lib';
import { TabsNavComponent } from '../../../../shared/tabs-nav/tabs-nav.component';
import { SuiProjectEditableSelectComponent } from '../../../../shared/sui-lib-customisations/sui-select/editable/project/sui-project-editable-select.component';

@Component({
    selector: 'winde-selected-area-info',
    templateUrl: './selected-area-info.component.html',
    styleUrls: ['../entities-info-window.scss'],
    imports: [FormsModule, SuiTranslateEnumPipe, SuiTranslatePipe, TabsNavComponent, SuiProjectEditableSelectComponent]
})
export class GtthSelectedAreaInfoComponent extends GtthEntityInfoWindowContent implements OnInit, OnDestroy {
    public static readonly CUSTOM_ELEMENT_TAG_NAME = 'gtth-selected-area-info-custom-tag-element';

    public static readonly POLE_BLOCK_PER_LOCATION_VALUE = -2;
    public static readonly DEVICE_AZIMUTH_PER_LOCATION_VALUE = -2;

    @Output()
    public closeCallback = new EventEmitter<void>();

    forceClose = false;

    public stepGroupEnum = GtthStepGroupEnum;
    public readonly linkTypes = LinkFeature.possibleLosStates;
    public readonly WHOLE_TOPOLOGY_LAYER_INDEX = GtthLogicalTopology.WHOLE_TOPOLOGY_LAYER_INDEX;

    public selectedToRemoveCounter: number;
    public selectedNodesIdToRemove: number[];
    public selectedLinksIdToRemove: number[];
    public selectedTerminalUnitsId: number[] = [];
    public selectedLinksId: number[] = [];
    public selectedEnableEntitiesCount = 0;
    public selectedEnableNodes: number[] = [];
    public selectedEnableLinks: number[] = [];
    public selectedDisableEntitiesCount = 0;
    public selectedDisableNodes: number[] = [];
    public selectedDisableLinks: number[] = [];
    public allowLosStationsCount: number;
    public losToVerifyCount: number;
    public usedProd0: number;
    public usedProd1: number;
    public usedAnt0: number;
    public usedAnt1: number;
    public polygonId: number;
    public freezedLinks: LinkFeature[];
    public unfreezedLinks: LinkFeature[];
    public installedLinks: LinkFeature[];
    public notInstalledLinks: LinkFeature[];
    public tabsToShow: string[];
    public tabsTranslationParams: Map<string, Record<string, string>> = new Map<string, Record<string, string>>();
    public currentTab: number;
    public areaInfoTabs = SelectedAreaInfoTabsEnum;
    public showHouseDetectionBtn: boolean;
    public showSavePolygonBtn: boolean;
    public showDeletePolygonBtn: boolean;

    public unfrozenNodesSelected = false;
    public mhTypeValues = [NodeTypeEnum.BEST_EFFORT, NodeTypeEnum.BU_MUST, NodeTypeEnum.OPTIONAL_BU, NodeTypeEnum.NO_BU];
    public mhType: NodeTypeEnum;

    public showDeviceAzimuthControl = false;

    public blockedByPoleControl = new SuiTypeEditableSelect<number>(undefined, [
        new SuiEditableSelectOption<number>('Per location', GtthSelectedAreaInfoComponent.POLE_BLOCK_PER_LOCATION_VALUE),
        new SuiEditableSelectOption<number>('No', GtthNodeFeatureDtoModel.POLE_BLOCKED_NO),
        new SuiEditableSelectOption<number>('Auto', GtthNodeFeatureDtoModel.DEFAULT_POLE_BLOCKED_ANGLE),
        new SuiEditableSelectOption<number>('Value', 20, new SuiTypeNumber(20, 20, 20, 70))
    ]);

    public deviceAzimuthControl = new SuiTypeEditableSelect<number>(undefined, [
        new SuiEditableSelectOption<number>('Per location', GtthSelectedAreaInfoComponent.DEVICE_AZIMUTH_PER_LOCATION_VALUE),
        new SuiEditableSelectOption<number>('Global', GtthNodeFeatureDtoModel.DEFAULT_BU_DEVICE_AZIMUTH),
        new SuiEditableSelectOption<number>('Value', 0, new SuiTypeNumber(0, 0, 0, 359))
    ]);

    public layerIndex: number;
    public layersOptions: GtthKeyValueDTO[];

    private changeLinksType: GtthChangeTypeForSelectedLiksCommand;
    private changeBlockedByPoleMultipleUnitsCommand: GtthChangeBlockedByPoleMultipleUnitsCommand;
    private changeDeviceAzimuthMultipleUnitsCommand: GtthChangeDeviceAzimuthMultipleUnitsCommand;

    constructor(
        private readonly notificationService: GtthNotificationsService,
        public readonly historyService: GtthHistoryService,
        public readonly projectStateService: GtthProjectStateService,
        infoWindowService: GtthInfoWindowsService
    ) {
        super();
        infoWindowService.registerActiveInfoWindow(this);
    }

    private _linkType: TopologyLinkTypeEnum;

    get linkType(): TopologyLinkTypeEnum {
        return this._linkType;
    }

    set linkType(value: TopologyLinkTypeEnum) {
        this._linkType = value;
        this.setLinksType();
    }

    ngOnInit() {
        if (isDefined(this.entityId)) {
            this.polygonId = this.entityId;
        } else {
            this.polygonId = undefined;
        }

        this.setPolygonActionsState();

        if (this.historyService.logicalTopology.hasSelectedEntities()) {
            if (this.historyService.selectedStepNumber === this.stepGroupEnum.LOCATIONS_AND_LOSES) {
                this.currentTab = this.areaInfoTabs.ALL;
                this.getSelectedEntities();
            } else {
                this.tabsToShow = [];
                this.getSelectedDesignEntities();
            }
        }

        if (this.historyService.logicalTopology.layersModeEnabled) {
            this.layersOptions = this.historyService.logicalTopology.layers.map((layer: GtthLayerDtoModel) => {
                return { key: layer.layerIndex, value: layer.getFullName() } as GtthKeyValueDTO;
            });
        }
    }

    ngOnDestroy() {
        if (isDefined(this.polygonId)) {
            const polygon = this.historyService.polygonsService.getStoredPolygon(this.polygonId);
            if (polygon) {
                polygon.unselect();
            }
        }
        if (this.changeBlockedByPoleMultipleUnitsCommand && this.blockedByPoleControl.validateValue()) {
            // apply command if blockedByPoleAngle is changed and is within range 0-360
            this.historyService.doCommand(this.changeBlockedByPoleMultipleUnitsCommand);
        }
        if (this.changeDeviceAzimuthMultipleUnitsCommand && this.deviceAzimuthControl.validateValue()) {
            // apply command if buDeviceAzimuth is changed and is within range -1-360
            this.historyService.doCommand(this.changeDeviceAzimuthMultipleUnitsCommand);
        }
        if (this.changeLinksType) {
            this.historyService.doCommand(this.changeLinksType);
        }
    }

    createLos() {
        const createLosesCommand = this.historyService.command(GtthCreateLosesToSelectedEntitiesCommand);
        this.historyService.doCommand(createLosesCommand).then((resolve: boolean) => {
            if (resolve) {
                this.allowLosStationsCount = this.historyService.logicalTopology.getAllowLosesForSelectedEntities().length;
                this.losToVerifyCount = this.historyService.logicalTopology.getExistLosForSelectedEntities().size;
            }
        });
    }

    verifyLos() {
        const createLosesCommand = this.historyService.command(GtthVerifyLosesSelectedEntitiesCommand).create();
        this.historyService.doCommand(createLosesCommand).then((resolve: boolean) => {
            if (resolve) {
                this.losToVerifyCount = this.historyService.logicalTopology.getExistLosForSelectedEntities().size;
            }
        });
    }

    removeAll(entitiesType?: number) {
        this.closeCallback.next();
        let removeMultipleEntitiesCommand;
        if (entitiesType) {
            switch (entitiesType) {
                case this.areaInfoTabs.LOCATIONS:
                    removeMultipleEntitiesCommand = this.historyService
                        .command(GtthRemoveMultipleEntitiesCommand)
                        .create(this.selectedNodesIdToRemove, undefined);
                    break;
                case this.areaInfoTabs.LINKS:
                    removeMultipleEntitiesCommand = this.historyService
                        .command(GtthRemoveMultipleEntitiesCommand)
                        .create([], this.selectedLinksIdToRemove);
                    break;
            }
        } else {
            removeMultipleEntitiesCommand = this.historyService.command(GtthRemoveMultipleEntitiesCommand);
        }

        if (removeMultipleEntitiesCommand) {
            this.historyService.doCommand(removeMultipleEntitiesCommand);
        }
    }

    getSelectedEntities() {
        this.selectedTerminalUnitsId = [];
        this.selectedEnableEntitiesCount = 0;
        this.selectedDisableEntitiesCount = 0;
        this.selectedLinksId = [];
        this.freezedLinks = [];
        this.installedLinks = [];
        this.selectedNodesIdToRemove = [];
        this.selectedLinksIdToRemove = [];
        this.selectedEnableNodes = [];
        this.selectedEnableLinks = [];
        this.selectedDisableNodes = [];
        this.selectedDisableLinks = [];

        this.mhType = null;
        this.layerIndex = -1;

        const stationsIterator = this.historyService.logicalTopology.getSelectedStations();
        for (const station of stationsIterator) {
            this.selectedTerminalUnitsId.push(station.getId());
            if (station.hasFreezeLinks()) {
                continue;
            }
            this.unfrozenNodesSelected = true;

            if (this.mhType === null) {
                //    if its first node without frozen links - just set node value
                this.mhType = station.getMhType();
                this.layerIndex = station.getLayerIndex();
            } else {
                this.mhType = this.mhType === undefined || this.mhType !== station.getMhType() ? undefined : station.getMhType();
                this.layerIndex =
                    this.layerIndex === undefined || this.layerIndex !== station.getLayerIndex() ? undefined : station.getLayerIndex();
            }

            this.setInitialBlockedByPoleValue(station);
            this.setInitialDeviceAzimuthValue(station);

            this.selectedNodesIdToRemove.push(station.getId());
            if (station.isEnabled()) {
                this.selectedEnableEntitiesCount += 1;
                this.selectedEnableNodes.push(station.getId());
            } else {
                this.selectedDisableEntitiesCount += 1;
                this.selectedDisableNodes.push(station.getId());
            }
        }

        this.showDeviceAzimuthControl = this.mhType === NodeTypeEnum.BU_MUST;

        this.sortLinksByType();

        this.selectedToRemoveCounter = this.selectedNodesIdToRemove.length + this.selectedLinksIdToRemove.length;

        this.allowLosStationsCount = this.historyService.logicalTopology.getAllowLosesForSelectedEntities().length;
        this.losToVerifyCount = this.historyService.logicalTopology.getExistLosForSelectedEntities().size;
        this.setTabs();
    }

    setBlockedByPoleAngle(blockedByPoleValue: number): void {
        if (blockedByPoleValue === GtthSelectedAreaInfoComponent.POLE_BLOCK_PER_LOCATION_VALUE) {
            this.changeBlockedByPoleMultipleUnitsCommand = undefined;
            return;
        }

        if (!this.changeBlockedByPoleMultipleUnitsCommand) {
            this.changeBlockedByPoleMultipleUnitsCommand = this.historyService
                .command(GtthChangeBlockedByPoleMultipleUnitsCommand)
                .create(this.selectedTerminalUnitsId, blockedByPoleValue);
        }

        this.changeBlockedByPoleMultipleUnitsCommand.setBlockedByPoleAngle(blockedByPoleValue);
    }

    setDeviceAzimuth(deviceAzimuthValue: number): void {
        if (deviceAzimuthValue === GtthSelectedAreaInfoComponent.DEVICE_AZIMUTH_PER_LOCATION_VALUE) {
            this.changeDeviceAzimuthMultipleUnitsCommand = undefined;
            return;
        }

        if (!this.changeDeviceAzimuthMultipleUnitsCommand) {
            this.changeDeviceAzimuthMultipleUnitsCommand = this.historyService
                .command(GtthChangeDeviceAzimuthMultipleUnitsCommand)
                .create(this.selectedTerminalUnitsId, deviceAzimuthValue);
        }

        this.changeDeviceAzimuthMultipleUnitsCommand.setBuDeviceAzimuth(deviceAzimuthValue);
    }

    getSelectedDesignEntities() {
        this.selectedTerminalUnitsId = [];
        this.selectedEnableEntitiesCount = 0;
        this.selectedDisableEntitiesCount = 0;
        this.selectedLinksId = [];
        this.freezedLinks = [];
        this.unfreezedLinks = [];
        this.installedLinks = [];
        this.notInstalledLinks = [];

        const linksIterator = this.historyService.logicalTopology.getSelectedLinks();
        for (const link of linksIterator) {
            if (link.getLinkType() !== TopologyLinkTypeEnum.LINK) {
                continue;
            }
            this.selectedLinksId.push(link.getId());
            if (link.isUserForced()) {
                if (link.isInstalled()) {
                    this.installedLinks.push(link);
                } else {
                    this.notInstalledLinks.push(link);
                    this.freezedLinks.push(link);
                }
            } else {
                this.unfreezedLinks.push(link);
            }
        }

        if (
            this.selectedLinksId.length === 0 &&
            !this.historyService.logicalTopology.isShowPlanningMode &&
            this.historyService.selectedStepNumber !== GtthStepGroupEnum.LOCATIONS_AND_LOSES
        ) {
            this.historyService.clearSelectionCloseWindowAndArea();
        }
    }

    toggleEnable(status: boolean, entitiesType?: number) {
        let toggleEnableCommand;
        if (status) {
            toggleEnableCommand = this.historyService.command(GtthEnableMultipleEntitiesCommand);
            if (entitiesType === this.areaInfoTabs.LOCATIONS) {
                const linksToEnable = [];
                this.selectedDisableNodes.forEach((stationId: number) => {
                    const stationLinks = this.historyService.logicalTopology.getAllStationLinks(stationId);
                    stationLinks.forEach((link) => {
                        if (linksToEnable.indexOf(link.getId()) === -1) {
                            linksToEnable.push(link.getId());
                        }
                    });
                });
                toggleEnableCommand = toggleEnableCommand.createFromList(this.selectedDisableNodes, linksToEnable);
            }
        } else {
            toggleEnableCommand = this.historyService.command(GtthDisableMultipleEntitiesCommand);
            if (entitiesType === this.areaInfoTabs.LINKS) {
                toggleEnableCommand = toggleEnableCommand.createFromList([], this.selectedEnableLinks);
            }
        }

        this.historyService.doCommand(toggleEnableCommand).then(() => {
            this.getSelectedEntities();
        });
    }

    toggleFreezeLinks(linkList: LinkFeature[], status: boolean, isInstalled: boolean) {
        const freezeLinksCommand = this.historyService
            .command(GtthFreezeSelectedLinksCommand)
            .createFromLinksList(linkList, status, isInstalled);
        this.historyService.doCommand(freezeLinksCommand).then((resolve: boolean) => {
            if (resolve) {
                if (this.historyService.selectedStepNumber === GtthStepGroupEnum.LOCATIONS_AND_LOSES) {
                    this.getSelectedEntities();
                } else {
                    this.getSelectedDesignEntities();
                }
            }
        });
    }

    isValid(): boolean {
        return this.blockedByPoleControl.validateValue() && this.deviceAzimuthControl.validateValue();
    }

    changeTab(tab: string) {
        this.currentTab = this.tabsToShow.indexOf(tab);
    }

    public savePolygon() {
        this.polygonId = this.historyService.polygonsService.savePolygon(this.historyService);
        this.tabsToShow = [];
        this.currentTab = undefined;
        this.historyService.logicalTopology.clearSelection();

        this.setPolygonActionsState();

        this.historyService.doCommandBypassHistory(this.historyService.command(GtthUpdateMainConfigCommand));
    }

    public removePolygon() {
        if (!isDefined(this.polygonId)) {
            return;
        }

        this.historyService.polygonsService.clearPolygon(this.polygonId);
        this.closeCallback.next();

        this.historyService.doCommandBypassHistory(this.historyService.command(GtthUpdateMainConfigCommand));
    }

    public startHouseDetection() {
        let path = [];
        const storedPolygon: PolygonFeature = this.historyService.polygonsService.getStoredPolygon(this.polygonId);

        if (isDefined(this.polygonId) && storedPolygon) {
            path = storedPolygon.getPath();
        } else if (isDefined(this.historyService.selectAreaService.overlay)) {
            path = this.historyService.selectAreaService.getSelectedAreaPath().map((point: GtthPosition) => point.toPositionDto());
        }

        if (!path || path.length === 0) {
            this.notificationService.showError('Please select area on map first');
            return;
        }

        const houseDetectionCommand = this.historyService.command(GtthHouseDetectionCommand).create([path]);
        this.historyService.doCommand(houseDetectionCommand);
    }

    public async changeMHType() {
        const changeMHTypeCommand = this.historyService
            .command(GtthChangeMHTypeMultipleUnitsCommand)
            .create(this.selectedTerminalUnitsId, this.mhType);
        await this.historyService.doCommand(changeMHTypeCommand);

        this.showDeviceAzimuthControl = this.mhType === NodeTypeEnum.BU_MUST;
        this.setDeviceAzimuth(GtthNodeFeatureDtoModel.DEFAULT_BU_DEVICE_AZIMUTH);
    }

    public changeLayerIndex(newLayerIndex: number) {
        this.layerIndex = newLayerIndex;
        const changeLayerCommand = this.historyService
            .command(GtthChangeLayerMultipleUnitsCommand)
            .create(this.selectedTerminalUnitsId, this.layerIndex);
        this.historyService.doCommand(changeLayerCommand);
    }

    private setInitialBlockedByPoleValue(station: NodeFeature): void {
        if (this.blockedByPoleControl.value === undefined) {
            this.blockedByPoleControl.setValue(station.getBlockedByPoleAngle());
        } else if (this.blockedByPoleControl.value !== GtthSelectedAreaInfoComponent.POLE_BLOCK_PER_LOCATION_VALUE) {
            const newValue =
                this.blockedByPoleControl.value === station.getBlockedByPoleAngle()
                    ? this.blockedByPoleControl.value
                    : GtthSelectedAreaInfoComponent.POLE_BLOCK_PER_LOCATION_VALUE;
            this.blockedByPoleControl.setValue(newValue);
        }
    }

    private setInitialDeviceAzimuthValue(station: NodeFeature): void {
        if (this.deviceAzimuthControl.value === undefined) {
            this.deviceAzimuthControl.setValue(station.getBuDeviceAzimuth());
        } else if (this.deviceAzimuthControl.value !== GtthSelectedAreaInfoComponent.DEVICE_AZIMUTH_PER_LOCATION_VALUE) {
            const newValue =
                this.deviceAzimuthControl.value === station.getBuDeviceAzimuth()
                    ? this.deviceAzimuthControl.value
                    : GtthSelectedAreaInfoComponent.DEVICE_AZIMUTH_PER_LOCATION_VALUE;
            this.deviceAzimuthControl.setValue(newValue);
        }
    }

    private sortLinksByType(): void {
        const linksIterator = this.historyService.logicalTopology.getSelectedLinks();
        for (const link of linksIterator) {
            this.selectedLinksId.push(link.getId());
            if (link.isUserForced()) {
                if (link.isInstalled()) {
                    this.installedLinks.push(link);
                } else {
                    this.freezedLinks.push(link);
                }
                continue;
            }

            this.selectedLinksIdToRemove.push(link.getId());
            if (!link.getLinkIsDisabled()) {
                this.selectedEnableEntitiesCount += 1;
                this.selectedEnableLinks.push(link.getId());
            } else {
                this.selectedDisableEntitiesCount += 1;
                this.selectedDisableLinks.push(link.getId());
            }
        }
    }

    private setTabs() {
        this.tabsToShow = [SelectedAreaInfoTabsEnum[this.areaInfoTabs.ALL]];
        if (this.selectedTerminalUnitsId && this.selectedTerminalUnitsId.length > 0) {
            const tabName = SelectedAreaInfoTabsEnum[this.areaInfoTabs.LOCATIONS];
            this.tabsToShow.push(tabName);
            this.tabsTranslationParams.set(tabName, { count: `(${this.selectedTerminalUnitsId.length})` });
        }
        if (this.selectedLinksId && this.selectedLinksId.length > 0) {
            const tabName = SelectedAreaInfoTabsEnum[this.areaInfoTabs.LINKS];
            this.tabsToShow.push(tabName);
            this.tabsTranslationParams.set(tabName, { count: `(${this.selectedLinksId.length})` });
        }
    }

    private setPolygonActionsState() {
        const hasOverlay = isDefined(this.historyService?.selectAreaService?.overlay);
        this.showSavePolygonBtn = hasOverlay;
        this.showDeletePolygonBtn = isDefined(this.polygonId) && !hasOverlay;
        this.showHouseDetectionBtn = isDefined(this.polygonId) || hasOverlay;
    }

    private setLinksType() {
        if (!this.changeLinksType) {
            this.changeLinksType = this.historyService.command(GtthChangeTypeForSelectedLiksCommand).create();
        }
        this.changeLinksType.setLinkType(this.linkType, this.usedProd0, this.usedProd1, this.usedAnt0, this.usedAnt1);
    }
}
